(() => {
  'use strict';

  const root = window.Shopify?.routes?.root || '/';
  const shopUrl = path => root + path.replace(/^\//, '');
  const announcer = document.querySelector('[data-fem-announcer]');
  const layer = document.querySelector('[data-fem-cart-layer]');
  const drawer = document.querySelector('[data-fem-cart-drawer]');
  const itemsRegion = document.querySelector('[data-fem-cart-items]');
  const cartStatus = document.querySelector('[data-fem-cart-status]');
  let previousFocus = null;
  let cartBusy = false;
  let cartCurrency = document.body.dataset.femMoneyCurrency || 'EUR';
  const inertBefore = new Map();

  function announce(message) {
    if (announcer) announcer.textContent = message;
  }

  function status(message, error = false) {
    if (!cartStatus) return;
    cartStatus.textContent = message;
    cartStatus.dataset.error = String(error);
  }

  function money(cents) {
    return new Intl.NumberFormat('fr-FR', {style: 'currency', currency: cartCurrency}).format(Number(cents || 0) / 100);
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  async function request(path, options = {}) {
    const response = await fetch(shopUrl(path), {
      credentials: 'same-origin',
      ...options,
      headers: {'Accept': 'application/json', ...(options.headers || {})}
    });
    const json = await response.json();
    if (!response.ok) throw new Error(json.description || json.message || 'La demande n’a pas abouti. Merci de réessayer.');
    return json;
  }

  function openCart() {
    if (!layer || !drawer || !layer.hidden) return;
    previousFocus = document.activeElement;
    layer.hidden = false;
    document.body.classList.add('fem-cart-is-open');
    document.querySelectorAll('[data-fem-page-region]').forEach(region => {
      inertBefore.set(region, region.inert);
      region.inert = true;
    });
    drawer.querySelector('[data-fem-cart-close]')?.focus();
  }

  function closeCart() {
    if (!layer || layer.hidden) return;
    layer.hidden = true;
    document.body.classList.remove('fem-cart-is-open');
    inertBefore.forEach((wasInert, region) => { region.inert = wasInert; });
    inertBefore.clear();
    if (previousFocus?.isConnected) previousFocus.focus();
  }

  function updateCounts(count) {
    document.querySelectorAll('[data-fem-cart-count]').forEach(node => {
      node.textContent = String(count);
      node.hidden = count === 0;
    });
  }

  function renderCart(cart) {
    if (!itemsRegion) return;
    cartCurrency = cart.currency || cartCurrency;
    updateCounts(cart.item_count);
    itemsRegion.replaceChildren();
    document.querySelector('[data-fem-cart-bottom]').hidden = cart.item_count === 0;
    document.querySelector('[data-fem-cart-total]').textContent = money(cart.total_price);

    if (!cart.item_count) {
      const empty = element('div', 'fem-cart-empty');
      empty.append(element('h3', '', 'Votre prochaine campagne commence ici.'));
      empty.append(element('p', '', 'Choisissez un fichier email par métier ou zone géographique pour préparer votre prospection.'));
      itemsRegion.append(empty);
      return;
    }

    cart.items.forEach(item => {
      const article = element('article', 'fem-cart-item');
      const image = element('img');
      if (item.image) {
        image.src = item.image;
        image.alt = '';
        image.loading = 'lazy';
        article.append(image);
      } else {
        const placeholder = element('span');
        placeholder.setAttribute('aria-hidden', 'true');
        article.append(placeholder);
      }
      const copy = element('div');
      const title = element('a', 'fem-cart-item-title', item.product_title || item.title);
      title.href = item.url;
      copy.append(title);
      if (item.variant_title && item.variant_title !== 'Default Title') copy.append(element('p', 'fem-cart-item-option', item.variant_title));
      const row = element('div', 'fem-cart-item-row');
      const quantity = element('div', 'fem-cart-quantity');
      const decrease = element('button', '', '−');
      decrease.type = 'button';
      decrease.setAttribute('aria-label', 'Diminuer la quantité de ' + item.product_title);
      decrease.dataset.cartAction = 'decrease';
      decrease.dataset.cartKey = item.key;
      decrease.addEventListener('click', () => changeQuantity(item.key, item.quantity - 1, 'decrease'));
      const count = element('span', '', String(item.quantity));
      count.setAttribute('aria-label', 'Quantité : ' + item.quantity);
      const increase = element('button', '', '+');
      increase.type = 'button';
      increase.setAttribute('aria-label', 'Augmenter la quantité de ' + item.product_title);
      increase.dataset.cartAction = 'increase';
      increase.dataset.cartKey = item.key;
      increase.addEventListener('click', () => changeQuantity(item.key, item.quantity + 1, 'increase'));
      quantity.append(decrease, count, increase);
      row.append(quantity, element('strong', 'fem-cart-item-price', money(item.final_line_price)));
      copy.append(row);
      const remove = element('button', 'fem-cart-remove', 'Retirer');
      remove.type = 'button';
      remove.setAttribute('aria-label', 'Retirer ' + item.product_title + ' du panier');
      remove.dataset.cartAction = 'remove';
      remove.dataset.cartKey = item.key;
      remove.addEventListener('click', () => changeQuantity(item.key, 0, 'remove'));
      copy.append(remove);
      article.append(copy);
      itemsRegion.append(article);
    });
    document.dispatchEvent(new CustomEvent('fem:cart:updated', {detail: {itemCount: cart.item_count}}));
  }

  async function loadCart() {
    status('Chargement du panier…');
    const cart = await request('cart.js');
    renderCart(cart);
    status('');
    return cart;
  }

  async function changeQuantity(key, quantity, action) {
    if (cartBusy) return;
    cartBusy = true;
    drawer.setAttribute('aria-busy', 'true');
    try {
      const cart = await request('cart/change.js', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({id: key, quantity})
      });
      renderCart(cart);
      status(quantity === 0 ? 'Le fichier a été retiré du panier.' : 'La quantité a été mise à jour.');
      const nextFocus = [...itemsRegion.querySelectorAll('[data-cart-key]')].find(node => node.dataset.cartKey === key && node.dataset.cartAction === action);
      (nextFocus || drawer.querySelector('[data-fem-cart-close]'))?.focus();
    } catch (error) {
      status(error.message, true);
    } finally {
      drawer.removeAttribute('aria-busy');
      cartBusy = false;
    }
  }

  document.addEventListener('click', async event => {
    const opener = event.target.closest('[data-fem-cart-open]');
    if (opener && layer) {
      event.preventDefault();
      openCart();
      try { await loadCart(); } catch (error) { status('Le panier est temporairement indisponible. Vous pouvez réessayer.', true); }
    }
    if (event.target.closest('[data-fem-cart-close]')) closeCart();
  });

  document.addEventListener('submit', async event => {
    const form = event.target.closest('form[data-fem-add], form[data-fem-cart-form]');
    if (!form || !layer) return;
    event.preventDefault();
    if (form.dataset.femSubmitting === 'true') return;
    form.dataset.femSubmitting = 'true';
    form.setAttribute('aria-busy', 'true');
    const button = event.submitter || form.querySelector('[type="submit"]');
    const errorNode = form.querySelector('[data-fem-cart-error]');
    if (errorNode) { errorNode.hidden = true; errorNode.textContent = ''; }
    if (button) button.disabled = true;
    announce('Ajout du fichier au panier.');
    try {
      await request('cart/add.js', {method: 'POST', body: new FormData(form)});
      openCart();
      await loadCart();
      status('Le fichier a été ajouté à votre sélection.');
      announce('Le fichier a été ajouté au panier.');
    } catch (error) {
      const message = error.message || 'L’ajout au panier n’a pas abouti. Merci de réessayer.';
      if (errorNode) { errorNode.textContent = message; errorNode.hidden = false; }
      status(message, true);
      announce(message);
    } finally {
      form.dataset.femSubmitting = 'false';
      form.removeAttribute('aria-busy');
      if (button) button.disabled = false;
    }
  });

  document.addEventListener('keydown', event => {
    if (!layer || layer.hidden) return;
    if (event.key === 'Escape') { event.preventDefault(); closeCart(); }
    if (event.key !== 'Tab') return;
    const focusable = [...drawer.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),[tabindex="0"]')].filter(node => !node.hidden && node.getClientRects().length);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  });

  const preparedHeaders = new WeakSet();
  function prepareHeaders() {
    document.querySelectorAll('[data-fem-header]').forEach(header => {
      if (preparedHeaders.has(header)) return;
      preparedHeaders.add(header);
      const menu = header.querySelector('[data-fem-navigation]');
      const toggle = header.querySelector('[data-fem-menu-toggle]');
      toggle?.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(open));
        toggle.setAttribute('aria-label', open ? 'Fermer le menu' : 'Ouvrir le menu');
        menu.classList.toggle('is-open', open);
      });
      const details = [...header.querySelectorAll('[data-fem-nav-details]')];
      details.forEach(current => current.addEventListener('toggle', () => {
        if (current.open) details.forEach(other => { if (other !== current) other.open = false; });
      }));
      function closeMenus() {
        details.forEach(detail => { detail.open = false; });
        menu.classList.remove('is-open');
        toggle?.setAttribute('aria-expanded', 'false');
        toggle?.setAttribute('aria-label', 'Ouvrir le menu');
      }
      document.addEventListener('click', event => { if (!header.contains(event.target)) closeMenus(); });
      header.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        const open = details.find(detail => detail.open);
        closeMenus();
        (open?.querySelector('summary') || toggle)?.focus();
      });
      prepareSearch(header);
    });
  }

  function prepareSearch(header) {
    const form = header.querySelector('[data-fem-search]');
    const input = header.querySelector('[data-fem-search-input]');
    const results = header.querySelector('[data-fem-search-results]');
    if (!form || !input || !results) return;
    let timer;
    let controller;
    let requestNumber = 0;
    function closeResults() { results.hidden = true; input.setAttribute('aria-expanded', 'false'); }
    input.addEventListener('input', () => {
      clearTimeout(timer);
      controller?.abort();
      const query = input.value.trim();
      requestNumber += 1;
      if (query.length < 2) { closeResults(); return; }
      const current = requestNumber;
      timer = setTimeout(async () => {
        controller = new AbortController();
        try {
          const params = new URLSearchParams({q: query, 'resources[type]': 'product', 'resources[limit]': '5', 'resources[options][unavailable_products]': 'last'});
          const data = await request('search/suggest.json?' + params, {signal: controller.signal});
          if (current !== requestNumber || !form.contains(document.activeElement)) return;
          results.replaceChildren();
          const products = data.resources?.results?.products || [];
          products.forEach(product => {
            const link = element('a', '', product.title);
            link.href = product.url;
            results.append(link);
          });
          if (!products.length) results.append(element('p', '', 'Aucune suggestion. Lancez la recherche pour explorer le catalogue.'));
          const all = element('a', 'fem-search-view-all', 'Voir tous les résultats');
          all.href = shopUrl('search') + '?type=product&q=' + encodeURIComponent(query);
          results.append(all);
          results.hidden = false;
          input.setAttribute('aria-expanded', 'true');
        } catch (error) { if (error.name !== 'AbortError') closeResults(); }
      }, 200);
    });
    form.addEventListener('keydown', event => {
      if (event.key === 'Escape') { closeResults(); input.focus(); }
      if (event.key === 'ArrowDown' && event.target === input && !results.hidden) {
        event.preventDefault(); results.querySelector('a')?.focus();
      }
      if (['ArrowDown', 'ArrowUp'].includes(event.key) && results.contains(event.target)) {
        const links = [...results.querySelectorAll('a')];
        const index = links.indexOf(event.target);
        event.preventDefault();
        if (event.key === 'ArrowUp' && index === 0) input.focus();
        else links[(index + (event.key === 'ArrowDown' ? 1 : -1) + links.length) % links.length]?.focus();
      }
    });
    document.addEventListener('click', event => { if (!form.contains(event.target)) closeResults(); });
    form.addEventListener('focusout', () => { setTimeout(() => { if (!form.contains(document.activeElement)) closeResults(); }, 0); });
  }

  prepareHeaders();
  document.addEventListener('shopify:section:load', prepareHeaders);
})();
